import axios from "axios";
import { UserData } from "../model/User";

// Cache verifyAccessToken results per accessToken+partner to avoid duplicate network calls.
const verifyCache = new Map<string, Promise<boolean>>();

const tokenService = {
  decryptUserDataToken: async (
    token: string,
    partnerName: string,
    route: Location
  ) => {
    const formData = new FormData();
    const decodedToken = decodeURIComponent(token);
    const fixedToken = decodedToken.replace(/ /g, "+");
    formData.append("token", fixedToken);
    formData.append("partner", partnerName);
    const response = await axios.post(
      process.env.REACT_APP_BASE_URL_V2 + "/Token/Decrypt",
      formData
    );

    const params = new URLSearchParams(route.search);

    // console.log({ response: response }); // for debugging

    if (response.data.IsSuccess && response.data.data) {
      const decrypted = response.data.data.split("&");

      if (!decrypted) {
        return undefined;
      }

      const innerObj: any = {};

      for (let i = 0; i < decrypted.length; i++) {
        const a = decrypted[i].split("=");
        innerObj[a[0].replace(" ", "")] = a[1];
      }

      const userData: UserData = {
        firstName: innerObj.firstname || "",
        lastName: innerObj.lastname || "",
        birthdate: innerObj.birthdate || innerObj.birthday || "",
        gender: innerObj.gender || "",
        tel:
          innerObj.tel ||
          params.get("mobile") ||
          params.get("mobilephone") ||
          "",
        email: innerObj.email || "",
        idCard: innerObj.idcard || innerObj.cardid || "",
        hn: innerObj.hn || "",
        accessToken: innerObj.accesstoken || "",
        idToken: innerObj.idToken || "",
        searchOption: innerObj.searchOption || "",
      };

      // Post process accessToken.
      if (userData.accessToken) {
        const accessGranted = await tokenService.verifyAccessToken(
          userData.accessToken,
          partnerName
        );
        if (!accessGranted) {
          userData.accessToken = "";
        }
      }

      return userData;
    } else {
      return undefined;
    }
  },

  verifyAccessToken: async (accessToken: string, partnerName: string) => {
    const cacheKey = `${partnerName}:${accessToken}`;
    if (!verifyCache.has(cacheKey)) {
      const formData = new FormData();
      formData.append("accessToken", accessToken);
      formData.append("partner", partnerName);
      const promise = axios
        .post(
          process.env.REACT_APP_BASE_URL_V2 + "/Token/VerifyAccessToken",
          formData
        )
        .then((r) => r.status === 200)
        .catch((error) => {
          if (axios.isAxiosError(error) && error.response) {
            const { status, data } = error.response;
            console.error(`Error ${status}: ${JSON.stringify(data)}`);
            if (status === 400 || status === 401) alert("Access denied.");
            else if (status === 403)
              alert("Token expired due to inactivity, please try again.");
            // 4xx responses are definitive — keep cached so we don't retry.
            return false;
          }
          // Transient failure (no response or 5xx): evict cache so the next
          // call can retry rather than permanently denying access.
          verifyCache.delete(cacheKey);
          console.error(`VerifyAccessToken error: ${error?.message}`);
          return false;
        });
      verifyCache.set(cacheKey, promise);
    }
    return verifyCache.get(cacheKey)!;
  },
};

// Utility function to get query parameters from a URL string
const getQueryParam = (param: string, search: string) => {
  const queryParams = new URLSearchParams(search);
  return queryParams.get(param) || "";
};

export { tokenService };
